import type { SVGAttributes } from "react";
import { generatePath } from "./utils";

export type ShapeProps = {
	width: number;
	height: number;
} & SVGAttributes<SVGElement>;

function Rectangle({ width, height, ...svgAttributes }: ShapeProps) {
	return <rect x={0} y={0} width={width} height={height} {...svgAttributes} />;
}

function Diamond({ width, height, ...svgAttributes }: ShapeProps) {
	const diamondPath = generatePath([
		[0, height / 2],
		[width / 2, 0],
		[width, height / 2],
		[width / 2, height],
	]);

	return <path d={diamondPath} {...svgAttributes} />;
}

export const ShapeComponents = {
	rectangle: Rectangle,
	diamond: Diamond,
};

export type ShapeType = keyof typeof ShapeComponents;

export type ShapeComponentProps = Partial<ShapeProps> & { type: ShapeType };

function Shape({ type, width, height, ...svgAttributes }: ShapeComponentProps) {
	const ShapeComponent = ShapeComponents[type];

	if (!(width && height && ShapeComponent)) {
		return null;
	}

	const strokeWidth = svgAttributes.strokeWidth
		? +svgAttributes.strokeWidth
		: 0;

	// we subtract the strokeWidth to make sure the shape is not cut off
	// this is done because svg doesn't support stroke inset (https://stackoverflow.com/questions/7241393/can-you-control-how-an-svgs-stroke-width-is-drawn)
	const innerWidth = width - 2 * strokeWidth;
	const innerHeight = height - 2 * strokeWidth;

	return (
		<svg width={width} height={height} className="shape-svg">
			{/* this offsets the shape by the strokeWidth so that we have enough space for the stroke */}
			<g
				transform={`translate(${svgAttributes.strokeWidth ?? 0}, ${
					svgAttributes.strokeWidth ?? 0
				})`}
			>
				<ShapeComponent
					width={innerWidth}
					height={innerHeight}
					{...svgAttributes}
				/>
			</g>
		</svg>
	);
}

export default Shape;
